/**
 * Welche Tage gehören zu einem Einsatz?
 *
 * Ein Einsatz ist ein Zeitraum von Start bis Ende. Samstag und Sonntag zählen
 * dabei nur, wenn sie ausdrücklich eingeplant sind (`weekdaysOnly === false`).
 * Liegt Beginn oder Ende selbst auf einem Wochenendtag, ist dieser Tag ebenfalls
 * ausdrücklich gewählt und gehört dazu.
 *
 * Dieselbe Regel nutzen Planungsboard, Einsatz-Dialog und Tagesbericht.
 */

export interface EinsatzZeitraum {
  start: string;
  end: string;
  weekdaysOnly?: boolean;
}

export function istWochenende(tag: Date): boolean {
  const d = tag.getDay();
  return d === 0 || d === 6;
}

function tagesbeginn(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Letzter Kalendertag des Einsatzes (Ende um 00:00 zählt zum Vortag). */
function letzterTag(e: EinsatzZeitraum): Date {
  const end = new Date(e.end);
  const tag = tagesbeginn(end);
  return end.getTime() === tag.getTime() && end > new Date(e.start)
    ? new Date(tag.getFullYear(), tag.getMonth(), tag.getDate() - 1)
    : tag;
}

/** Berührt der Zeitraum diesen Kalendertag? (ohne Wochenendregel) */
export function liegtImZeitraum(e: EinsatzZeitraum, tag: Date): boolean {
  const beginn = tagesbeginn(tag);
  const naechster = new Date(beginn.getFullYear(), beginn.getMonth(), beginn.getDate() + 1);
  return new Date(e.start) < naechster && new Date(e.end) > beginn;
}

/**
 * Wochenende ausdrücklich eingeplant? Ja, wenn so gespeichert – oder wenn Beginn
 * bzw. Ende selbst auf Sa/So liegen (dann wurde das Wochenende bewusst gewählt).
 */
export function wochenendeEingeplant(e: EinsatzZeitraum): boolean {
  return e.weekdaysOnly === false
    || istWochenende(new Date(e.start))
    || istWochenende(letzterTag(e));
}

/** Ist dieser Kalendertag ein geplanter Einsatztag? */
export function istEinsatztag(e: EinsatzZeitraum, tag: Date): boolean {
  if (!liegtImZeitraum(e, tag)) {
    return false;
  }

  return !istWochenende(tag) || wochenendeEingeplant(e);
}

/** Wochenendtage im Zeitraum, die (noch) nicht eingeplant sind. */
export function freieWochenendtage(e: EinsatzZeitraum): Date[] {
  const tage: Date[] = [];
  const ende = letzterTag(e);
  for (let d = tagesbeginn(new Date(e.start)); d <= ende; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    if (istWochenende(d) && !istEinsatztag(e, d)) {
      tage.push(d);
    }
  }
  return tage;
}

/** Enthält der Zeitraum überhaupt einen Samstag oder Sonntag? */
export function enthaeltWochenende(e: EinsatzZeitraum): boolean {
  const ende = letzterTag(e);
  for (let d = tagesbeginn(new Date(e.start)); d <= ende; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    if (istWochenende(d)) {
      return true;
    }
  }
  return false;
}

/** Anzahl der geplanten Einsatztage. */
export function anzahlEinsatztage(e: EinsatzZeitraum): number {
  let anzahl = 0;
  const ende = letzterTag(e);
  for (let d = tagesbeginn(new Date(e.start)); d <= ende; d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)) {
    if (istEinsatztag(e, d)) {
      anzahl++;
    }
  }
  return anzahl;
}
